// Topical-authority cluster data for "ChatGPT Ads for South Florida Home Services".
// Built per the Julian Goldie hub-and-spoke method (see fame-topical-authority MAP.md)
// + the ChatGPT Ads dossier (real May-2026 facts: self-serve ads.openai.com, $50K min
// removed, ZIP geo-targeting, $3-5 CPC, context-hints-not-keywords).
//
// CORE VERTICALS (exactly 6, NO remodeling): Roofing, HVAC, Plumbing, Electrical,
// Garage Door, Fire/Water/Mold Restoration.

export const SITE_ORIGIN = 'https://floridaaimarketingexperts.com';
export const PHONE = '+1-954-296-4896';
export const PHONE_DISPLAY = '(954) 296-4896';
export const WHATSAPP = 'https://wa.me/19542964896';

export interface Vertical {
  slug: string;            // used in /chatgpt-ads-for-<slug>-florida
  name: string;            // "Roofers"
  trade: string;           // "roofing"
  hint: string;            // context-hint example conversation
  job: string;             // strongest service line
  dealLow: number;
  dealHigh: number;
}

export const VERTICALS: Vertical[] = [
  {
    slug: 'roofers',
    name: 'Roofers',
    trade: 'roofing',
    hint: '"homeowner with a roof leak after a Florida storm asking who to call"',
    job: 'storm-damage roof replacement',
    dealLow: 9000,
    dealHigh: 30000,
  },
  {
    slug: 'hvac-contractors',
    name: 'HVAC Contractors',
    trade: 'HVAC',
    hint: '"homeowner whose AC stopped cooling in Florida heat asking repair vs replace"',
    job: 'AC replacement',
    dealLow: 6000,
    dealHigh: 14000,
  },
  {
    slug: 'plumbers',
    name: 'Plumbers',
    trade: 'plumbing',
    hint: '"homeowner with a burst pipe or water heater failure asking for emergency service"',
    job: 'repipe and water-heater work',
    dealLow: 2000,
    dealHigh: 12000,
  },
  {
    slug: 'electricians',
    name: 'Electricians',
    trade: 'electrical',
    hint: '"homeowner needing a panel upgrade or losing power asking for a licensed electrician"',
    job: 'panel upgrade and rewire',
    dealLow: 2500,
    dealHigh: 9000,
  },
  {
    slug: 'garage-door-companies',
    name: 'Garage Door Companies',
    trade: 'garage door',
    hint: '"homeowner with a broken garage-door spring or opener asking for same-day repair"',
    job: 'garage-door and opener replacement',
    dealLow: 500,
    dealHigh: 4500,
  },
  {
    slug: 'restoration-companies',
    name: 'Restoration Companies',
    trade: 'fire, water & mold restoration',
    hint: '"homeowner with flooding, fire damage or mold asking for 24/7 restoration"',
    job: 'water, fire and mold remediation',
    dealLow: 3000,
    dealHigh: 40000,
  },
];

export function getVertical(slug: string) {
  return VERTICALS.find((v) => v.slug === slug);
}

export interface ClusterCity {
  slug: string;       // /chatgpt-ads-<slug>
  name: string;       // "Miami"
  county: string;     // "Miami-Dade"
  metro: string;      // "South Florida"
}

export const CLUSTER_CITIES: ClusterCity[] = [
  { slug: 'miami', name: 'Miami', county: 'Miami-Dade', metro: 'South Florida' },
  { slug: 'fort-lauderdale', name: 'Fort Lauderdale', county: 'Broward', metro: 'South Florida' },
  { slug: 'palm-beach', name: 'West Palm Beach', county: 'Palm Beach', metro: 'South Florida' },
];

export function getClusterCity(slug: string) {
  return CLUSTER_CITIES.find((c) => c.slug === slug);
}

// Shared real-fact stat bank (dossier-sourced) for fact-density cadence.
export const FACTS = {
  selfServe: 'OpenAI opened a free self-serve Ads Manager at ads.openai.com in May 2026.',
  noMinimum: 'The earlier $50,000 minimum spend was removed, so a contractor can test for a few hundred dollars.',
  cpc: 'OpenAI recommends a starting max bid of $3–5 per click on the Clicks objective.',
  geo: 'The Ads Manager now supports state, DMA, and ZIP-code geo-targeting — sharper than Google Ads default city radius.',
  hints: 'Targeting uses plain-language "context hints" describing the conversation, not exact-match keywords.',
  audience: 'Ads appear only to logged-in adults on the Free and Go ($8/mo) ChatGPT tiers, below the answer, labeled "Sponsored."',
  trust: 'OpenAI states ads do not influence ChatGPT\'s answers — paid placement runs on a separate system.',
  attribution: 'Conversion tracking is directional in the first 30 days: a pixel and Conversions API exist, but no CallRail/ServiceTitan booked-job attribution yet.',
  conversionRate: 'AI-referred traffic converts materially higher than traditional search — cited near 14.2% versus 2.8% for Google.',
  geoLift: 'JSON-LD schema and fact-dense content can lift a business\'s visibility in AI answers by up to ~40%.',
};
